import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Orcamento } from '../../core/models/workflow.models';
import { OrcamentosService } from '../../core/services/orcamentos.service';

type OrcamentoStatus =
  | 'Aberto'
  | 'Aguardando retorno'
  | 'Efetuar Contato'
  | 'Perdido'
  | 'Ganho';

interface StatusSummary {
  status: OrcamentoStatus;
  count: number;
  total: number;
  oldest: Orcamento[];
}

@Component({
  selector: 'app-relatorios-gestao-funil-orcamentos',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './relatorios-gestao-funil-orcamentos.component.html',
  styleUrl: './relatorios-gestao-funil-orcamentos.component.scss',
})
export class RelatoriosGestaoFunilOrcamentosComponent implements OnInit, OnDestroy {
  readonly statusColumns: OrcamentoStatus[] = [
    'Aberto',
    'Aguardando retorno',
    'Efetuar Contato',
    'Perdido',
    'Ganho',
  ];

  orcamentos: Orcamento[] = [];
  filtered: Orcamento[] = [];
  statusSummary: StatusSummary[] = [];
  tableItems: Orcamento[] = [];

  filterStart = '';
  filterEnd = '';
  filterStatus: 'TODOS' | OrcamentoStatus = 'TODOS';
  filterConsultor = 'TODOS';

  consultores: string[] = [];

  total = 0;
  ganhos = 0;
  perdidos = 0;
  conversao = 0;
  mediaGanho = 0;
  mediaPerdido = 0;

  private subscription = new Subscription();

  constructor(private orcamentosService: OrcamentosService) {}

  ngOnInit(): void {
    this.subscription.add(
      this.orcamentosService.getAll().subscribe((orcamentos) => {
        this.orcamentos = orcamentos;
        this.consultores = this.buildConsultores(orcamentos);
        this.applyFilters();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  applyFilters(): void {
    const start = this.filterStart ? new Date(`${this.filterStart}T00:00:00`) : null;
    const end = this.filterEnd ? new Date(`${this.filterEnd}T23:59:59`) : null;

    this.filtered = this.orcamentos.filter((orcamento) => {
      const status = this.getStatusAtendimento(orcamento);
      const consultor = this.getConsultorNome(orcamento);
      const abertura = this.getDataAbertura(orcamento);

      if (this.filterStatus !== 'TODOS' && status !== this.filterStatus) {
        return false;
      }
      if (this.filterConsultor !== 'TODOS' && consultor !== this.filterConsultor) {
        return false;
      }
      if (start && abertura < start) {
        return false;
      }
      if (end && abertura > end) {
        return false;
      }
      return true;
    });

    this.rebuildSummary();
  }

  private rebuildSummary(): void {
    const now = new Date();
    this.total = this.filtered.length;
    this.ganhos = this.filtered.filter((item) => this.getStatusAtendimento(item) === 'Ganho').length;
    this.perdidos = this.filtered.filter((item) => this.getStatusAtendimento(item) === 'Perdido').length;
    this.conversao = this.total ? (this.ganhos / this.total) * 100 : 0;

    const ganhosItens = this.filtered.filter((item) => this.getStatusAtendimento(item) === 'Ganho');
    const perdidosItens = this.filtered.filter((item) => this.getStatusAtendimento(item) === 'Perdido');
    this.mediaGanho = this.calcMediaDias(ganhosItens);
    this.mediaPerdido = this.calcMediaDias(perdidosItens);

    this.statusSummary = this.statusColumns.map((status) => {
      const items = this.filtered.filter((item) => this.getStatusAtendimento(item) === status);
      const oldest = [...items]
        .sort((a, b) => this.getDiasEmAberto(b, now) - this.getDiasEmAberto(a, now))
        .slice(0, 3);
      return {
        status,
        count: items.length,
        total: items.reduce((acc, item) => acc + this.getValor(item), 0),
        oldest,
      };
    });

    this.tableItems = [...this.filtered].sort(
      (a, b) => this.getDiasEmAberto(b, now) - this.getDiasEmAberto(a, now)
    );
  }

  private buildConsultores(items: Orcamento[]): string[] {
    const set = new Set<string>();
    items.forEach((item) => {
      const nome = this.getConsultorNome(item);
      if (nome && nome !== '—') {
        set.add(nome);
      }
    });
    return Array.from(set).sort();
  }

  getStatusAtendimento(orcamento: Orcamento): OrcamentoStatus {
    const status = (orcamento as Orcamento & { statusAtendimento?: string }).statusAtendimento;
    if (status === 'Aberto' || status === 'Aguardando retorno' || status === 'Efetuar Contato' || status === 'Perdido' || status === 'Ganho') {
      return status;
    }
    return 'Aberto';
  }

  getStatusColor(status: OrcamentoStatus): string {
    switch (status) {
      case 'Aberto':
        return '#2563eb';
      case 'Aguardando retorno':
        return '#7c3aed';
      case 'Efetuar Contato':
        return '#f59e0b';
      case 'Perdido':
        return '#ef4444';
      case 'Ganho':
        return '#22c55e';
      default:
        return '#64748b';
    }
  }

  formatNumeroOrcamento(orcamento: Orcamento): string {
    const candidate =
      (orcamento as Orcamento & { numeroOrcamento?: string | number }).numeroOrcamento ??
      (orcamento as Orcamento & { numero?: string | number }).numero ??
      (orcamento as Orcamento & { codigo?: string | number }).codigo ??
      (orcamento as Orcamento & { codigoExterno?: string | number }).codigoExterno;

    if (candidate !== undefined && candidate !== null && String(candidate).trim()) {
      return String(candidate);
    }

    return `ORC-${String(orcamento.id).padStart(6, '0')}`;
  }

  getVeiculoLabel(orcamento: Orcamento): string {
    const placa = orcamento.placa || '';
    const marca = orcamento.marca || '';
    const modelo = orcamento.modelo || '';
    const detalhes = [marca, modelo].filter(Boolean).join(' ');
    return detalhes ? `${placa} - ${detalhes}` : placa;
  }

  getValor(orcamento: Orcamento): number {
    const valor =
      (orcamento as Orcamento & { valorOrcamento?: number }).valorOrcamento ??
      (orcamento as Orcamento & { valor?: number }).valor ??
      (orcamento as Orcamento & { total?: number }).total ??
      0;
    return Number(valor) || 0;
  }

  formatCurrency(valor: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  }

  getConsultorNome(orcamento: Orcamento): string {
    const direct =
      (orcamento as Orcamento & { consultorResponsavel?: string }).consultorResponsavel ??
      (orcamento as Orcamento & { consultor?: string }).consultor ??
      (orcamento as Orcamento & { responsavelAtual?: string }).responsavelAtual ??
      (orcamento as Orcamento & { consultorNome?: string }).consultorNome;

    if (direct && String(direct).trim()) {
      return String(direct).trim();
    }

    const nested = (orcamento as Orcamento & { consultor?: { nome?: string } }).consultor?.nome;
    if (nested && nested.trim()) {
      return nested.trim();
    }

    return '—';
  }

  getDataAbertura(orcamento: Orcamento): Date {
    const raw =
      (orcamento as Orcamento & { dataCriacao?: Date | string }).dataCriacao ?? orcamento.criadoEm;
    return raw instanceof Date ? raw : new Date(raw);
  }

  getDataFechamento(orcamento: Orcamento): Date {
    const raw =
      (orcamento as Orcamento & { dataFechamento?: Date | string }).dataFechamento ??
      (orcamento as Orcamento & { dataPrevistaEntrega?: Date | string }).dataPrevistaEntrega;
    if (raw) {
      return raw instanceof Date ? raw : new Date(raw);
    }
    const abertura = this.getDataAbertura(orcamento);
    const fallback = new Date(abertura);
    fallback.setDate(fallback.getDate() + 3);
    return fallback;
  }

  getDiasEmAberto(orcamento: Orcamento, now = new Date()): number {
    const abertura = this.getDataAbertura(orcamento);
    const diff = Math.max(0, now.getTime() - abertura.getTime());
    return Math.floor(diff / (24 * 60 * 60 * 1000));
  }

  private calcMediaDias(items: Orcamento[]): number {
    if (!items.length) {
      return 0;
    }
    const total = items.reduce((acc, item) => {
      const abertura = this.getDataAbertura(item);
      const fechamento = this.getDataFechamento(item);
      const diff = Math.max(0, fechamento.getTime() - abertura.getTime());
      return acc + diff / (24 * 60 * 60 * 1000);
    }, 0);
    return Math.round(total / items.length);
  }
}

